/**
 * Catálogo c_FormaPago del SAT (CFDI 4.0) y deducción del código a partir de un
 * método de pago. Espejo de `src/shared/utils/formas_pago_sat.py` en siga-backend:
 * si cambias las reglas aquí, cámbialas allá.
 */
import type { MetodoPagoConfig } from '../services/configuracion.service';

export interface FormaPagoSat {
  clave: string;
  descripcion: string;
}

export const FORMAS_PAGO_SAT: FormaPagoSat[] = [
  { clave: '01', descripcion: 'Efectivo' },
  { clave: '02', descripcion: 'Cheque nominativo' },
  { clave: '03', descripcion: 'Transferencia electrónica de fondos' },
  { clave: '04', descripcion: 'Tarjeta de crédito' },
  { clave: '05', descripcion: 'Monedero electrónico' },
  { clave: '06', descripcion: 'Dinero electrónico' },
  { clave: '08', descripcion: 'Vales de despensa' },
  { clave: '12', descripcion: 'Dación en pago' },
  { clave: '13', descripcion: 'Pago por subrogación' },
  { clave: '14', descripcion: 'Pago por consignación' },
  { clave: '15', descripcion: 'Condonación' },
  { clave: '17', descripcion: 'Compensación' },
  { clave: '23', descripcion: 'Novación' },
  { clave: '24', descripcion: 'Confusión' },
  { clave: '25', descripcion: 'Remisión de deuda' },
  { clave: '26', descripcion: 'Prescripción o caducidad' },
  { clave: '27', descripcion: 'A satisfacción del acreedor' },
  { clave: '28', descripcion: 'Tarjeta de débito' },
  { clave: '29', descripcion: 'Tarjeta de servicios' },
  { clave: '30', descripcion: 'Aplicación de anticipos' },
  { clave: '31', descripcion: 'Intermediario pagos' },
  { clave: '99', descripcion: 'Por definir' },
];

const SAT_POR_METODO_BASE: Record<string, string> = {
  efectivo: '01',
  tarjeta: '04',
  transferencia: '03',
  credito: '99',
};

const norm = (v: unknown) =>
  String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

export function descripcionFormaPago(clave: string | null | undefined): string {
  return FORMAS_PAGO_SAT.find(f => f.clave === clave)?.descripcion ?? '';
}

/** Deduce la forma de pago del nombre del método. '' si no hay forma de saberlo. */
export function codigoSatPorNombre(nombre: string | null | undefined): string {
  const n = norm(nombre);
  if (!n) return '';
  const base = SAT_POR_METODO_BASE[n.toLowerCase()];
  if (base) return base;
  if (n.includes('DEBITO')) return '28';
  if (n.includes('CHEQUE')) return '02';
  if (n.includes('TRANSFER') || n.includes('SPEI')) return '03';
  if (n.includes('EFECTIVO')) return '01';
  if (n.includes('VALE')) return '08';
  if (n.includes('MONEDERO')) return '05';
  if (n.includes('ANTICIPO')) return '30';
  // "Tarjeta de crédito" antes que "crédito" a secas (fiado = por definir).
  if (n.includes('TARJETA')) return '04';
  if (n.includes('CREDITO')) return '99';
  return '';
}

function codigoDeMetodo(m: MetodoPagoConfig): string {
  return (m.codigo_sat || '').trim()
    || SAT_POR_METODO_BASE[(m.id || '').toLowerCase()]
    || codigoSatPorNombre(m.nombre);
}

export const CODIGO_CREDITO = '99';

/** Renglones del conteo físico del corte de caja. */
export type GrupoArqueo = 'efectivo' | 'tarjeta' | 'otros';
export type EsperadoPorConcepto = Record<GrupoArqueo, number>;

const ETIQUETAS: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Cheque',
  '03': 'Transferencia',
  '04': 'Tarjeta de crédito',
  '28': 'Tarjeta de débito',
  '29': 'Tarjeta de servicios',
  '99': 'Crédito (por cobrar)',
};

/** Nombre corto del concepto para cortes y reportes: '28' -> 'Tarjeta de débito'. */
export function etiquetaFormaPago(clave: string | null | undefined): string {
  const c = (clave || '').trim();
  if (!c) return 'Sin clasificar';
  return ETIQUETAS[c] || descripcionFormaPago(c) || `Forma ${c}`;
}

/** Renglón del arqueo. null = crédito (no es dinero recibido). */
export function grupoArqueo(clave: string | null | undefined): GrupoArqueo | null {
  const c = (clave || '').trim();
  if (c === CODIGO_CREDITO) return null;
  if (c === '01') return 'efectivo';
  if (c === '04' || c === '28' || c === '29') return 'tarjeta';
  return 'otros';
}

/** Código SAT de un pago/abono/movimiento: el guardado o el deducido de su método. */
export function formaPagoDe(
  p: { metodo?: string | null; forma_pago_sat?: string | null } | null | undefined,
  metodos: MetodoPagoConfig[] = [],
): string {
  if (!p) return '';
  return (p.forma_pago_sat || '').trim() || resolverFormaPagoSat(p.metodo, metodos);
}

/** Crédito = código 99 (o el id histórico CREDITO), aunque el método tenga id propio. */
export function esCreditoMetodo(m: MetodoPagoConfig): boolean {
  return (m.id || '').toLowerCase() === 'credito' || codigoDeMetodo(m) === CODIGO_CREDITO;
}

/**
 * Lo que debería haber en cada renglón del arqueo. Espejo de
 * `esperado_por_concepto` del backend: fondo + efectivo − salidas en efectivo;
 * tarjeta = crédito/débito/servicios; otros = transferencias, cheques…
 * Las entradas/salidas manuales (sin método) son del cajón: efectivo.
 */
export function esperadoPorConcepto(
  sesion: { monto_inicial?: number; movimientos?: { tipo: string; monto: number; metodo?: string; forma_pago_sat?: string }[] },
  metodos: MetodoPagoConfig[] = [],
): EsperadoPorConcepto {
  const tot: EsperadoPorConcepto = { efectivo: +(sesion.monto_inicial || 0), tarjeta: 0, otros: 0 };
  for (const m of sesion.movimientos || []) {
    if (m.tipo !== 'VENTA' && m.tipo !== 'ENTRADA' && m.tipo !== 'SALIDA') continue;
    const grupo = (m.forma_pago_sat || m.metodo) ? grupoArqueo(formaPagoDe(m, metodos)) : 'efectivo';
    if (!grupo) continue;
    tot[grupo] += (m.tipo === 'SALIDA' ? -1 : 1) * (+m.monto || 0);
  }
  return {
    efectivo: +tot.efectivo.toFixed(2),
    tarjeta: +tot.tarjeta.toFixed(2),
    otros: +tot.otros.toFixed(2),
  };
}

/**
 * Forma de pago SAT de un pago según su `metodo` (id del método, o su nombre en
 * mayúsculas en ventas viejas). Busca primero en la configuración del taller.
 */
export function resolverFormaPagoSat(metodo: string | null | undefined, metodos: MetodoPagoConfig[] = []): string {
  if (!metodo) return '';
  const buscado = String(metodo).trim();
  const porId = metodos.find(m => (m.id || '').trim() === buscado);
  if (porId) return codigoDeMetodo(porId);
  const n = norm(buscado);
  const porNombre = metodos.find(m => norm(m.nombre) === n || norm(m.id) === n);
  if (porNombre) return codigoDeMetodo(porNombre);
  return codigoSatPorNombre(buscado);
}
